// src/pages/auth/NewPassword.jsx
import React, { useState } from 'react';
import { Form, Input, Button, Typography, message } from 'antd';
import { EyeInvisibleOutlined, EyeTwoTone } from '@ant-design/icons';
import { Link, useParams, useNavigate } from 'react-router-dom';
import axiosInstance from '../../utils/axios';

const { Title } = Typography;

const NewPassword = () => {
  const { token } = useParams(); // Extracts token from URL: /password-reset/:token
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const onFinish = async (values) => {
    if (values.password !== values.confirm_password) {
      message.error('Passwords do not match!');
      return;
    }

    setLoading(true);

    try {
      await axiosInstance.post('accounts/reset-password/', {
        new_password: values.password,
        confirm_password: values.confirm_password,
        token: token, // Send token in payload
      });

      message.success('Password reset successfully! You can now log in.');
      setTimeout(() => navigate('/'), 2000); // Redirect after 2s
    } catch (error) {
      const errMsg =
        error.response?.data?.message ||
        error.response?.data?.new_password?.[0] ||
        error.response?.data?.token?.[0] ||
        error.response?.data?.detail ||
        'Failed to reset password. Link may be invalid or expired.';

      message.error(errMsg);
    } finally {
      setLoading(false);
    }
  };

  const onFinishFailed = (errorInfo) => {
    console.log('Failed:', errorInfo);
  };

  return (
    <>
      {/* Logo and branding */}
      <div style={{ textAlign: 'center', marginBottom: 32 }}>
        <div
          style={{
            width: 48,
            height: 48,
            margin: '0 auto 8px',
            backgroundColor: '#6366f1',
            borderRadius: 12,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <span style={{ color: 'white', fontWeight: 'bold' }}>∞</span>
        </div>
        <Title level={3} style={{ marginBottom: 0 }}>
          Welcome to Getam
        </Title>
      </div>

      <div style={{ marginBottom: 24 }}>
        <Title level={4} style={{ margin: 0 }}>
          Set New Password
        </Title>
        <div style={{ marginTop: 8, color: '#666', fontSize: 14 }}>
          Enter your new password below.
        </div>
      </div>

      <Form
        name="new-password"
        layout="vertical"
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
      >
        <Form.Item
          label="New Password"
          name="password"
          rules={[
            { required: true, message: 'Please enter your new password!' },
            { min: 6, message: 'Password must be at least 6 characters' },
          ]}
        >
          <Input.Password
            size="large"
            placeholder="Enter new password"
            iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
            style={{ borderRadius: 8 }}
          />
        </Form.Item>

        <Form.Item
          label="Confirm Password"
          name="confirm_password"
          dependencies={['password']}
          rules={[
            { required: true, message: 'Please confirm your password!' },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue('password') === value) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error('Passwords do not match!'));
              },
            }),
          ]}
        >
          <Input.Password
            size="large"
            placeholder="Re-enter password"
            iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
            style={{ borderRadius: 8 }}
          />
        </Form.Item>

        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 24,
          }}
        >
          <div></div>
          <Link to="/">← Back to Login</Link>
        </div>

        <Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            size="large"
            block
            loading={loading}
            disabled={loading}
            style={{
              backgroundColor: '#6366f1',
              borderColor: '#6366f1',
              borderRadius: 8,
              height: 48,
            }}
          >
            {loading ? 'Resetting...' : 'Reset Password'}
          </Button>
        </Form.Item>
      </Form>
    </>
  );
};

export default NewPassword;